import { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import cronstrue from 'cronstrue';
import { ToolLayout } from '../ToolLayout';

const EXAMPLES = [
  { expr: '* * * * *', label: 'Every minute' },
  { expr: '0 * * * *', label: 'Every hour' },
  { expr: '0 0 * * *', label: 'Every day at midnight' },
  { expr: '0 9 * * 1-5', label: 'Weekdays at 9am' },
  { expr: '0 0 1 * *', label: '1st of every month' },
  { expr: '0 0 * * 0', label: 'Every Sunday at midnight' },
  { expr: '*/15 * * * *', label: 'Every 15 minutes' },
  { expr: '0 2 * * 1', label: 'Every Monday at 2am' },
];

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5), maxWidth: 640 },
  inputHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  resultPaper: {
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  description: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  partsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  partBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    textAlign: 'center',
  },
  partValue: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  partLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  examplesSection: {
    marginTop: theme.spacing(1),
  },
  examplesTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  examplesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  exampleChip: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1.5),
    cursor: 'pointer',
    fontSize: 12,
    fontFamily: '"Fira Code","Consolas",monospace',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.1)' : '#FDE8F5',
    },
  },
}));

const PART_LABELS = ['Minute', 'Hour', 'Day', 'Month', 'Weekday'];

export function CronParserPage() {
  const { classes } = useStyles();
  const [expression, setExpression] = useState('');
  const [description, setDescription] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedExpr, setCopiedExpr] = useState(false);
  const [copiedDesc, setCopiedDesc] = useState(false);

  useEffect(() => {
    const val = expression.trim();
    if (!val) { setDescription(null); setError(null); return; }
    try {
      const result = cronstrue.toString(val, { use24HourTimeFormat: true, verbose: true });
      setDescription(result);
      setError(null);
    } catch (e: any) {
      setDescription(null);
      setError(e?.message ?? 'Invalid cron expression');
    }
  }, [expression]);

  const parts = expression.trim().split(/\s+/);
  const hasValidParts = parts.length >= 5 && !error;

  return (
    <ToolLayout
      title="Cron Parser"
      subtitle="Translate a cron expression into plain English"
    >
      <Box className={classes.layout}>
        <Box>
          <Box className={classes.inputHeader}>
            <Typography className={classes.paneLabel}>Cron Expression</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={copiedExpr ? 'Copied!' : 'Copy expression'}>
                <span>
                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(expression); setCopiedExpr(true); setTimeout(() => setCopiedExpr(false), 2000); }} disabled={!expression}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear">
                <span>
                  <IconButton size="small" onClick={() => setExpression('')} disabled={!expression}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="e.g.  0 9 * * 1-5"
            value={expression}
            onChange={e => setExpression(e.target.value)}
            inputProps={{
              spellCheck: false,
              style: { fontFamily: '"Fira Code","Consolas",monospace', fontSize: 15, letterSpacing: '0.05em' },
            }}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {description && (
          <Paper className={classes.resultPaper} elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Typography className={classes.description}>{description}</Typography>
              <Tooltip title={copiedDesc ? 'Copied!' : 'Copy description'}>
                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(description); setCopiedDesc(true); setTimeout(() => setCopiedDesc(false), 2000); }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {hasValidParts && (
              <Box className={classes.partsGrid}>
                {PART_LABELS.map((label, i) => (
                  <Box key={label} className={classes.partBox}>
                    <Typography className={classes.partValue}>{parts[i] ?? '*'}</Typography>
                    <Typography className={classes.partLabel}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* Quick examples */}
        <Box className={classes.examplesSection}>
          <Typography className={classes.examplesTitle}>Quick examples</Typography>
          <Box className={classes.examplesGrid}>
            {EXAMPLES.map(ex => (
              <Tooltip key={ex.expr} title={ex.label}>
                <span
                  className={classes.exampleChip}
                  onClick={() => setExpression(ex.expr)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setExpression(ex.expr)}
                >
                  {ex.expr}
                </span>
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>
    </ToolLayout>
  );
}
